import React, { useState, useEffect } from 'react';

// ─── Constants ───────────────────────────────────────────────────────────────

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_REGULAR_HOURS = [
  { day: 'Monday',    open: '09:00', close: '17:00', isClosed: false },
  { day: 'Tuesday',   open: '09:00', close: '17:00', isClosed: false },
  { day: 'Wednesday', open: '09:00', close: '17:00', isClosed: false },
  { day: 'Thursday',  open: '09:00', close: '17:00', isClosed: false },
  { day: 'Friday',    open: '09:00', close: '17:00', isClosed: false },
  { day: 'Saturday',  open: '09:00', close: '13:00', isClosed: false },
  { day: 'Sunday',    open: '09:00', close: '13:00', isClosed: true  },
];

const HOURS_OPTIONS   = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES_OPTIONS = [0, 15, 30, 45];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** '14:30' → { hours: 2, minutes: 30, period: 'PM' } */
function to12h(time24) {
  if (!time24) return { hours: 9, minutes: 0, period: 'AM' };
  const [h, m] = time24.split(':').map(Number);
  return { hours: h % 12 || 12, minutes: m, period: h >= 12 ? 'PM' : 'AM' };
}

/** (2, 30, 'PM') → '14:30' */
function to24h(hours, minutes, period) {
  let h = Number(hours);
  if (period === 'PM' && h < 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Build the flat time-input state from an array of regularHours */
function buildTimeInputs(rh) {
  return rh.map(d => {
    const op = to12h(d.open);
    const cl = to12h(d.close);
    return {
      hours:        op.hours,
      minutes:      op.minutes,
      period:       op.period,
      closeHours:   cl.hours,
      closeMinutes: cl.minutes,
      closePeriod:  cl.period,
    };
  });
}

// ─── Singapore Public Holidays ───────────────────────────────────────────────

async function fetchSingaporeHolidays(year = new Date().getFullYear()) {
  try {
    const res = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/SG`);
    if (!res.ok) throw new Error('API error');
    const data = await res.json();
    return data.map(h => ({ date: h.date, name: h.localName || h.name }));
  } catch {
    return getFallbackHolidays(year);
  }
}

function getFallbackHolidays(year) {
  return [
    { date: `${year}-01-01`, name: "New Year's Day" },
    { date: `${year}-02-10`, name: "Chinese New Year" },
    { date: `${year}-02-11`, name: "Chinese New Year (Day 2)" },
    { date: `${year}-03-29`, name: "Good Friday" },
    { date: `${year}-05-01`, name: "Labour Day" },
    { date: `${year}-05-22`, name: "Vesak Day" },
    { date: `${year}-06-17`, name: "Hari Raya Puasa" },
    { date: `${year}-08-09`, name: "National Day" },
    { date: `${year}-08-24`, name: "Hari Raya Haji" },
    { date: `${year}-10-24`, name: "Deepavali" },
    { date: `${year}-12-25`, name: "Christmas Day" },
  ];
}

// ─── Migration: old BespokeBridge format → new format ────────────────────────

/**
 * Old format: { schedule: { monday: { open: bool, openTime, closeTime } }, specialClosures: [], notes }
 * New format: { regularHours: [...], holidayClosures: [...], customClosures: [...], notes }
 */
function migrateOfficeHours(data) {
  if (!data) return null;

  // Already new format
  if (Array.isArray(data.regularHours)) return data;

  // Migrate from old schedule object
  const regularHours = DAYS.map(day => {
    const key = day.toLowerCase();
    const old = data.schedule?.[key];
    if (old) {
      return {
        day,
        open:     old.openTime  || '09:00',
        close:    old.closeTime || '17:00',
        isClosed: !old.open,
      };
    }
    return DEFAULT_REGULAR_HOURS.find(d => d.day === day);
  });

  // Map old specialClosures → customClosures
  const customClosures = (data.specialClosures || []).map(c => ({
    id:        c.id || String(Date.now() + Math.random()),
    startDate: c.date  || '',
    endDate:   c.date  || '',
    isFullDay: true,
    startTime: '09:00',
    endTime:   '17:00',
    reason:    c.name  || '',
    notes:     c.note  || '',
  }));

  return {
    regularHours,
    holidayClosures: [],
    customClosures,
    notes: data.notes || '',
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

function OfficeHoursEditor({ officeHours, onUpdate, onSave, saving, savedMessage }) {
  const [activeSection,   setActiveSection]   = useState('regular');
  const [regularHours,    setRegularHours]    = useState(DEFAULT_REGULAR_HOURS);
  const [timeInputs,      setTimeInputs]      = useState(buildTimeInputs(DEFAULT_REGULAR_HOURS));
  const [holidayClosures, setHolidayClosures] = useState([]);
  const [customClosures,  setCustomClosures]  = useState([]);
  const [notes,           setNotes]           = useState('');
  const [sgHolidays,      setSgHolidays]      = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(true);

  // Initialise from prop (handles both old & new formats)
  useEffect(() => {
    if (!officeHours) return;
    const migrated = migrateOfficeHours(officeHours);
    if (!migrated) return;

    const rh = DAYS.map(day => {
      const found = migrated.regularHours.find(d => d.day === day);
      return found || DEFAULT_REGULAR_HOURS.find(d => d.day === day);
    });

    setRegularHours(rh);
    setTimeInputs(buildTimeInputs(rh));
    setHolidayClosures(migrated.holidayClosures || []);
    setCustomClosures(migrated.customClosures   || []);
    setNotes(migrated.notes || '');
  }, [officeHours]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch Singapore holidays once
  useEffect(() => {
    fetchSingaporeHolidays()
      .then(h  => setSgHolidays(h))
      .finally(() => setLoadingHolidays(false));
  }, []);

  // Centralised notify-parent helper
  const notify = (rh, hc, cc, n) => {
    onUpdate({ regularHours: rh, holidayClosures: hc, customClosures: cc, notes: n });
  };

  // ── Regular Hours ──────────────────────────────────────────────────────────

  const handleClosedToggle = (index) => {
    const updated = regularHours.map((d, i) =>
      i === index ? { ...d, isClosed: !d.isClosed } : d
    );
    setRegularHours(updated);
    notify(updated, holidayClosures, customClosures, notes);
  };

  const handleTimeChange = (index, field, value) => {
    const updInputs = timeInputs.map((t, i) => i === index ? { ...t, [field]: value } : t);
    setTimeInputs(updInputs);
    const t = updInputs[index];
    const updated = regularHours.map((d, i) =>
      i !== index ? d : {
        ...d,
        open:  to24h(t.hours,      t.minutes,      t.period),
        close: to24h(t.closeHours, t.closeMinutes, t.closePeriod),
      }
    );
    setRegularHours(updated);
    notify(updated, holidayClosures, customClosures, notes);
  };

  // ── Holiday Closures ───────────────────────────────────────────────────────

  const handleHolidayToggle = (date, name) => {
    const exists  = holidayClosures.some(h => h.date === date);
    const updated = exists
      ? holidayClosures.filter(h => h.date !== date)
      : [...holidayClosures, { date, name, isClosed: true }];
    setHolidayClosures(updated);
    notify(regularHours, updated, customClosures, notes);
  };

  // ── Custom Closures ────────────────────────────────────────────────────────

  const addCustomClosure = () => {
    const today   = new Date().toISOString().split('T')[0];
    const updated = [
      ...customClosures,
      { id: String(Date.now()), startDate: today, endDate: today,
        isFullDay: true, startTime: '09:00', endTime: '18:00',
        reason: '', notes: '' },
    ];
    setCustomClosures(updated);
    notify(regularHours, holidayClosures, updated, notes);
  };

  const updateCustomClosure = (id, field, value) => {
    const updated = customClosures.map(c => c.id === id ? { ...c, [field]: value } : c);
    setCustomClosures(updated);
    notify(regularHours, holidayClosures, updated, notes);
  };

  const removeCustomClosure = (id) => {
    const updated = customClosures.filter(c => c.id !== id);
    setCustomClosures(updated);
    notify(regularHours, holidayClosures, updated, notes);
  };

  const handleNotesChange = (val) => {
    setNotes(val);
    notify(regularHours, holidayClosures, customClosures, val);
  };

  // ── Render: Regular Hours ──────────────────────────────────────────────────

  const renderRegularHours = () => (
    <div className="bb-oh-regular-section">
      <div className="bb-oh-section-desc">
        Set your organisation's standard operating hours for each day of the week.
      </div>

      <div className="bb-oh-hours-table">
        {/* Table header */}
        <div className="bb-oh-hours-header">
          <div className="bb-oh-col-day">Day</div>
          <div className="bb-oh-col-hours">Hours</div>
          <div className="bb-oh-col-closed">Closed</div>
        </div>

        {/* Day rows */}
        {regularHours.map((dayHours, index) => (
          <div
            key={dayHours.day}
            className={`bb-oh-hours-row${dayHours.isClosed ? ' bb-oh-hours-row--closed' : ''}`}
          >
            <div className="bb-oh-col-day bb-oh-day-label">
              {dayHours.day}
              <span className="bb-oh-required">*</span>
            </div>

            <div className="bb-oh-col-hours">
              {dayHours.isClosed ? (
                <span className="bb-oh-closed-text">Closed</span>
              ) : (
                <div className="bb-oh-time-pickers">
                  {/* Open time */}
                  <div className="bb-oh-time-picker">
                    <select
                      className="bb-oh-select"
                      value={timeInputs[index].hours}
                      onChange={e => handleTimeChange(index, 'hours', +e.target.value)}
                    >
                      {HOURS_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <span className="bb-oh-colon">:</span>
                    <select
                      className="bb-oh-select"
                      value={timeInputs[index].minutes}
                      onChange={e => handleTimeChange(index, 'minutes', +e.target.value)}
                    >
                      {MINUTES_OPTIONS.map(m =>
                        <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                      )}
                    </select>
                    <select
                      className="bb-oh-select bb-oh-select--period"
                      value={timeInputs[index].period}
                      onChange={e => handleTimeChange(index, 'period', e.target.value)}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>

                  <span className="bb-oh-to-sep">to</span>

                  {/* Close time */}
                  <div className="bb-oh-time-picker">
                    <select
                      className="bb-oh-select"
                      value={timeInputs[index].closeHours}
                      onChange={e => handleTimeChange(index, 'closeHours', +e.target.value)}
                    >
                      {HOURS_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <span className="bb-oh-colon">:</span>
                    <select
                      className="bb-oh-select"
                      value={timeInputs[index].closeMinutes}
                      onChange={e => handleTimeChange(index, 'closeMinutes', +e.target.value)}
                    >
                      {MINUTES_OPTIONS.map(m =>
                        <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                      )}
                    </select>
                    <select
                      className="bb-oh-select bb-oh-select--period"
                      value={timeInputs[index].closePeriod}
                      onChange={e => handleTimeChange(index, 'closePeriod', e.target.value)}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="bb-oh-col-closed">
              <label className="bb-oh-checkbox">
                <input
                  type="checkbox"
                  checked={dayHours.isClosed}
                  onChange={() => handleClosedToggle(index)}
                />
                <span className="bb-oh-checkmark" />
              </label>
            </div>
          </div>
        ))}
      </div>

      <p className="bb-oh-hours-note">
        Check the "Closed" box for days when your organisation is not open.
      </p>
    </div>
  );

  // ── Render: Holiday Closures ───────────────────────────────────────────────

  const renderHolidayClosures = () => (
    <div className="bb-oh-holiday-section">
      <div className="bb-oh-section-desc">
        Select which Singapore public holidays your organisation will be closed.
        Dates are automatically updated each year.
      </div>

      {loadingHolidays ? (
        <div className="bb-oh-loading">Loading Singapore public holidays…</div>
      ) : (
        <div className="bb-oh-holidays-list">
          {sgHolidays.map(holiday => {
            const selected = holidayClosures.some(h => h.date === holiday.date);
            return (
              <div
                key={holiday.date}
                className={`bb-oh-holiday-item${selected ? ' bb-oh-holiday-item--selected' : ''}`}
              >
                <label className="bb-oh-holiday-label">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => handleHolidayToggle(holiday.date, holiday.name)}
                  />
                  <div className="bb-oh-holiday-info">
                    <span className="bb-oh-holiday-name">{holiday.name}</span>
                    <span className="bb-oh-holiday-date">
                      {new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-SG', {
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                      })}
                    </span>
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Render: Custom Closures ────────────────────────────────────────────────

  const renderCustomClosures = () => (
    <div className="bb-oh-custom-section">
      <div className="bb-oh-custom-header">
        <div className="bb-oh-section-desc" style={{ margin: 0 }}>
          Add custom closure periods for renovations, staff leave, or special circumstances.
        </div>
        <button className="bb-btn-primary" onClick={addCustomClosure}>
          + Add Closure Period
        </button>
      </div>

      <div className="bb-oh-custom-list">
        {customClosures.length === 0 ? (
          <div className="bb-oh-empty">No custom closure periods set.</div>
        ) : (
          customClosures.map(closure => (
            <div key={closure.id} className="bb-oh-custom-item">
              <div className="bb-oh-custom-form">

                {/* Row 1: dates + full-day toggle */}
                <div className="bb-form-row" style={{ marginBottom: 16 }}>
                  <div className="bb-form-group" style={{ margin: 0 }}>
                    <label>Start Date</label>
                    <input
                      type="date"
                      className="bb-input"
                      value={closure.startDate}
                      onChange={e => updateCustomClosure(closure.id, 'startDate', e.target.value)}
                    />
                  </div>
                  <div className="bb-form-group" style={{ margin: 0 }}>
                    <label>End Date</label>
                    <input
                      type="date"
                      className="bb-input"
                      value={closure.endDate}
                      onChange={e => updateCustomClosure(closure.id, 'endDate', e.target.value)}
                    />
                  </div>
                  <div className="bb-form-group bb-oh-fullday-group" style={{ margin: 0 }}>
                    <label className="bb-oh-fullday-label">
                      <input
                        type="checkbox"
                        checked={closure.isFullDay}
                        onChange={e => updateCustomClosure(closure.id, 'isFullDay', e.target.checked)}
                      />
                      Full Day Closure
                    </label>
                  </div>
                </div>

                {/* Row 2: times (only if not full day) */}
                {!closure.isFullDay && (
                  <div className="bb-form-row" style={{ marginBottom: 16 }}>
                    <div className="bb-form-group" style={{ margin: 0 }}>
                      <label>Start Time</label>
                      <input
                        type="time"
                        className="bb-input"
                        value={closure.startTime}
                        onChange={e => updateCustomClosure(closure.id, 'startTime', e.target.value)}
                      />
                    </div>
                    <div className="bb-form-group" style={{ margin: 0 }}>
                      <label>End Time</label>
                      <input
                        type="time"
                        className="bb-input"
                        value={closure.endTime}
                        onChange={e => updateCustomClosure(closure.id, 'endTime', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* Row 3: Reason */}
                <div className="bb-form-group">
                  <label>Reason</label>
                  <input
                    type="text"
                    className="bb-input"
                    value={closure.reason}
                    onChange={e => updateCustomClosure(closure.id, 'reason', e.target.value)}
                    placeholder="e.g. Renovation, Staff Training, Annual Leave"
                  />
                </div>

                {/* Row 4: Notes */}
                <div className="bb-form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    className="bb-textarea"
                    rows={2}
                    value={closure.notes}
                    onChange={e => updateCustomClosure(closure.id, 'notes', e.target.value)}
                    placeholder="Additional details about this closure…"
                  />
                </div>

                {/* Actions */}
                <div className="bb-oh-custom-actions">
                  <button
                    className="bb-btn-danger-sm"
                    onClick={() => removeCustomClosure(closure.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div className="bb-editor">
      {/* Header */}
      <div className="bb-editor-header">
        <div>
          <h2>Office Hours &amp; Closure</h2>
          <p className="bb-editor-desc">
            Set your organisation's operating hours and manage closures for public holidays or special events.
          </p>
        </div>
        <div className="bb-editor-actions">
          {savedMessage && (
            <span className={`bb-save-msg${savedMessage.includes('Error') ? ' error' : ''}`}>
              {savedMessage}
            </span>
          )}
          <button className="bb-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Sub-section navigation */}
      <div className="bb-oh-section-nav">
        {[
          { key: 'regular',  label: 'Regular Hours'     },
          { key: 'holidays', label: 'Holiday Closures'  },
          { key: 'custom',   label: 'Custom Closures'   },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`bb-oh-nav-btn${activeSection === key ? ' active' : ''}`}
            onClick={() => setActiveSection(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Active section content */}
      <div className="bb-form-section">
        {activeSection === 'regular'  && renderRegularHours()}
        {activeSection === 'holidays' && renderHolidayClosures()}
        {activeSection === 'custom'   && renderCustomClosures()}
      </div>

      {/* Additional Notes — always visible */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Additional Notes</h3>
        <div className="bb-form-group">
          <label htmlFor="ohNotes">Message for Members (optional)</label>
          <textarea
            id="ohNotes"
            rows={3}
            className="bb-textarea"
            placeholder="e.g. For urgent matters outside office hours, please call our emergency line at…"
            value={notes}
            onChange={e => handleNotesChange(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

export default OfficeHoursEditor;
